// Which Home view a FARM member sees — "My Farm" or "My fruit at <packhouse>" —
// and when the app may change it behind their back (FM11 D1).
//
// The choice lives in `kf49.appMode` (usePersistedState). Two rules used to
// throw it away, so "My fruit" reset to "My Farm" on every return to Home, a
// bounce from a page included:
//
//   1. The default-the-view effect tracked the last tenant in a per-MOUNT ref,
//      so every remount of Home looked like a tenant switch.
//   2. The clamp ("this person cannot see the packhouse view") ran while the
//      current user and the packhouse links were still loading. An admin with a
//      link looked like a non-admin with none, for a beat, and was clamped.
//
// The memory below is module-level: it lives as long as the page does. A
// reload, the next day or a business switch finds it empty or different, so
// they still land on My Farm; a return to Home inside the same page keeps the
// view the person chose.

let defaultedFor: string | null = null

/** True the FIRST time Home sees this business in this page's life — and only
 *  then may the view be reset to the business kind's default. */
export function claimHomeViewDefault(tenantId: string): boolean {
  if (defaultedFor === tenantId) return false
  defaultedFor = tenantId
  return true
}

/** Tests only: a fresh page. */
export function forgetHomeViewDefault(): void {
  defaultedFor = null
}

export interface ClampInput {
  isFarmer: boolean
  appMode: 'packhouse' | 'farm'
  /** The current user's read has ANSWERED (success or error), not pending. */
  meAnswered: boolean
  /** The linked-packhouses read has answered. A paused or loading read has not. */
  linksAnswered: boolean
  canSeePackhouseView: boolean
  hasPackhouseLink: boolean
}

/** Whether a stale 'packhouse' choice must be forced back to the farm surface.
 *  Held until BOTH reads have answered: a default `[]` and an unloaded user are
 *  "don't know yet", never "not allowed". */
export function mustClampToFarm(i: ClampInput): boolean {
  if (!i.isFarmer || i.appMode !== 'packhouse') return false
  if (!i.meAnswered || !i.linksAnswered) return false
  return !i.canSeePackhouseView || !i.hasPackhouseLink
}
